import URLParseURL from 'url-parse';

export interface IParsedUrlParts {
  hostname: string;
  port: string;
  protocol: string;
  username: string;
  password: string;
  pathname: string;
  search: string;
  hash: string;
}

export interface IDerivedUrlParts {
  href: string;
  host: string;
  origin: string;
}

export interface IPartialLocationObject extends Partial<IParsedUrlParts>, Partial<IDerivedUrlParts> {}

function createParsedUrl(url: string, base?: string | IPartialLocationObject): URLParseURL {
  if (typeof url !== 'string') {
    // Normally we would just rely on types for this, but there is still some
    // non-typed code using this, and because missing this is particularly
    // dangerous in the case of `url-parse` because it will accept literally
    // anything as input without throwing an error, and silently produce a
    // result that doesn't make any sense.
    throw new Error('url must be a string');
  }

  const parsedUrl = new URLParseURL(
    url,
    base || {}, // Prevent url-parse from using window.location as the default base location.
    false, // Prevent url-parse from parsing query string itself.
  );

  return parsedUrl;
}

/**
 * Converts a string to an object representing the parts of the URL.
 *
 * Unlike the native `URL` constructor, this uses very loose parsing that will
 * accept any input and do the best it can, instead of throwing an error on any
 * bad input.
 */
export function parseUrlLoose(
  url: string,
  base?: string | IPartialLocationObject,
): IParsedUrlParts {
  const parsedUrl = createParsedUrl(url, base);

  // The types for url-parse are wrong, because they assume that `.query` will
  // always be an object, which is not true in our case because we pass the
  // third argument to the URLParseURL constructor to prevent it from parsing
  // the query string.
  //
  // To work around this, we convert it to an `unknown` type and then manually
  // assert that it is a string.
  const query: unknown = parsedUrl.query;
  if (typeof query !== 'string') {
    throw new Error('Internal error: expected url-parse query to be a string');
  }

  return {
    protocol: parsedUrl.protocol,
    hostname: parsedUrl.hostname,
    port: parsedUrl.port,
    username: parsedUrl.username,
    password: parsedUrl.password,
    pathname: parsedUrl.pathname,
    search: query,
    hash: parsedUrl.hash,
  };
}

/**
 * Converts an object representation of a URL to a full URL string.
 */
export function getFullUrl(location: IPartialLocationObject): string {
  return getDerivedUrlParts(location).href;
}

export function getDerivedUrlParts(location: IPartialLocationObject): IDerivedUrlParts {
  const url = createParsedUrl('');

  const orderedUrlParts: Array<keyof IPartialLocationObject> = [
    'href',
    'origin',
    'host',
    'protocol',
    'hostname',
    'port',
    'username',
    'password',
    'pathname',
    'search',
    'hash',
  ];

  // Ordered from broader to more specific
  //
  // The order is important, because broader properties like `origin` and `host`
  // will overwrite more specific ones like `hostname` and `port`.
  //
  // For example:
  //
  // ```
  // const broadToNarrow = new URLParseURL('');
  // broadToNarrow.host = 'locahost';
  // broadToNarrow.port = 3000;
  // broadToNarrow.href; // 'localhost:3000'
  //
  // const narrowToBroad = new URLParseURL('');
  // narrowToBroad.port = 3000;
  // narrowToBroad.host = 'locahost'; // `host` overwites both `hostname` and `port`
  // narrowToBroad.href; // 'localhost'
  // ```
  for (const key of orderedUrlParts) {
    const urlPart = location[key];
    if (!urlPart) {
      continue;
    }

    if (key === 'search') {
      url.set(
        'query',
        urlPart,
        (query: string): any => query, // Prevent url-parse from parsing query string.
      );
    } else {
      url.set(key, urlPart);
    }
  }
  const { href, origin, host } = url;
  return { href, origin, host };
}
